import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { formatNumber } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { CardAccountCard, CardCard, CardProgramCard, CopyInput, Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';
import { formatDateTime, formatString } from '~/util';

import { CardTransactionProps } from './CardTransaction';

interface Params {
  id: string;
}

const formatAmount = (amount?: { amount: number; currencyCode: string }): string => {
  if (!amount) {
    return '-';
  }

  return formatNumber(amount.amount, amount.currencyCode);
};

export const PageCardTransactionInformation: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { cardTransaction: outletCardTransaction } = useOutletContext<CardTransactionProps>();

  const { data: cardTransaction, error: cardTransactionError } = useQuery({
    queryKey: ['card-transaction', id],
    queryFn: () => CardTransactionRepository.get(id!),
    enabled: !!id && !outletCardTransaction,
    initialData: outletCardTransaction,
  });

  useEffect(() => {
    if (cardTransactionError) {
      navigate(ROUTE.CARD_TRANSACTIONS);
      addDangerNotification({
        content: (cardTransactionError as any).message || 'Failed to load card transaction',
      });
    }
  }, [cardTransactionError, navigate, addDangerNotification]);

  if (!cardTransaction) {
    return null;
  }

  return (
    <Inner p={0}>
      <Section headline="Information">
        <Inner px="var(--page-margin)" py={16}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Merchant Name</div>
              <div>
                <CopyInput value={cardTransaction.cardAcceptorDetails?.name || '-'} />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Type</div>
              <div>
                <CopyInput value={cardTransaction.type ? formatString(cardTransaction.type) : '-'} />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Card Program</div>
              <div>
                {cardTransaction.cardProgramId ? (
                  <CardProgramCard cardProgramId={cardTransaction.cardProgramId} />
                ) : (
                  <CopyInput value="-" />
                )}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Card Account</div>
              <div>
                {cardTransaction.cardAccountId ? (
                  <CardAccountCard cardAccountId={cardTransaction.cardAccountId} />
                ) : (
                  <CopyInput value="-" />
                )}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Card</div>
              <div>
                {cardTransaction.cardId ? <CardCard cardId={cardTransaction.cardId} /> : <CopyInput value="-" />}
              </div>
            </div>

            {cardTransaction.originalCardTransactionId && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Original Transaction</div>
                <div>
                  <CopyInput value={cardTransaction.originalCardTransactionId} isMonospaced />
                  <button
                    type="button"
                    style={{ marginTop: 8, cursor: 'pointer' }}
                    onClick={() =>
                      navigate(`${ROUTE.CARD_TRANSACTIONS}/edit/${cardTransaction.originalCardTransactionId}`)
                    }
                  >
                    View Original Transaction
                  </button>
                </div>
              </div>
            )}

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Cardholder Authorized</div>
              <div>
                <CopyInput value={formatAmount(cardTransaction.totalAmounts?.cardholderAuthorized)} />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Cardholder Settled</div>
              <div>
                <CopyInput value={formatAmount(cardTransaction.totalAmounts?.cardholderSettled)} />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Network Transaction ID</div>
              <div>
                <CopyInput value={cardTransaction.networkTransactionId || '-'} isMonospaced />
              </div>
            </div>

            {cardTransaction.authorizationExpiresAt && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Authorization Expires At</div>
                <div>
                  <CopyInput
                    value={formatDateTime(cardTransaction.authorizationExpiresAt, { timeZoneFormat: 'utc' })}
                  />
                </div>
              </div>
            )}

            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Created At</div>
              <div>
                <CopyInput value={formatDateTime(cardTransaction.createdAt, { timeZoneFormat: 'utc' })} />
              </div>
            </div>
          </div>
        </Inner>
      </Section>
    </Inner>
  );
};
